/**
 * The car detail screen: parts, their forecast, and the quick actions.
 *
 * This is where the owner/crew split is most visible (§6), and the rule it
 * follows is: **crew see every maintenance action and no structural control.**
 * Crew log service, mark inspections checked, and read everything. They do not
 * see "Add part", "Add car", or a delete anywhere — those routes are owner-only
 * (routes/fleet.ts, routes/items.ts) and a button that 403s is a broken promise.
 *
 * The forecast comes from `GET /garages/:id/timeline?carId=…`, filtered
 * server-side. Per-item status is reassembled from that response with the
 * engine's own rule (lib/display) because the API does not put its `statusByItem`
 * map on the wire.
 */

import { useState } from 'react';
import { CalendarDays, Check, CircleDashed, MapPin, Package, Pencil, Plus, RotateCcw, ShieldCheck } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import type { CarDto, ItemDto, TimelineResponse } from '@svcdue/contracts';

import { useCars, useItems, useLogService, useMarkChecked, useTimeline, useUndoService } from '../api/hooks';
import { listServiceLogs } from '../api/endpoints';
import { keys } from '../api/keys';
import { Empty, ErrorState, Loading } from '../components/states';
import { Eyebrow, Pill, Trigger } from '../components/primitives';
import { InspectCard } from '../components/InspectCard';
import { MidEventControl } from '../components/MidEventControl';
import { CarPhoto } from '../garage/CarPhoto';
import { useGarageContext, usePermissions } from '../garage/GarageProvider';
import { useSchedule } from '../lib/schedule';
import { daysUntil, fmtShort, today } from '../lib/format';
import { firstOccurrence, groupBySystem, projectionNote, statusByItem, triggerLabels } from '../lib/display';

export function Garage({
  openAddPart,
  openAddCar,
  openEditCar,
  openEditPart,
  openAddSafety,
}: {
  openAddPart: (car: CarDto) => void;
  openAddCar: () => void;
  openEditCar: (car: CarDto) => void;
  openEditPart: (car: CarDto, item: ItemDto) => void;
  openAddSafety: (car: CarDto) => void;
}) {
  const { garageId } = useGarageContext();
  const { canManageFleet } = usePermissions();
  const { carId } = useParams<{ carId: string }>();
  const navigate = useNavigate();

  const cars = useCars(garageId);
  // Default to the first car when the route names none.
  const car = cars.data?.find((c) => c.id === carId) ?? cars.data?.[0];

  if (cars.isPending) return <Loading label="Loading garage…" />;
  if (cars.isError) return <ErrorState error={cars.error} onRetry={() => void cars.refetch()} />;

  if (!cars.data || cars.data.length === 0) {
    return (
      <div className="screen">
        <Eyebrow>Garage</Eyebrow>
        <Empty
          title="No cars in this garage"
          hint={canManageFleet ? 'Add a car and its wear parts to start forecasting.' : 'The owner has not added a car yet.'}
          action={
            canManageFleet ? (
              <button className="act" onClick={openAddCar}>
                <Plus size={12} /> Add car
              </button>
            ) : undefined
          }
        />
      </div>
    );
  }

  if (!car) return <Loading />;

  return (
    <div className="screen">
      <Eyebrow>Garage</Eyebrow>
      <div className="car-switch">
        {cars.data.map((c) => (
          <button
            key={c.id}
            className={'cs-btn' + (c.id === car.id ? ' cs-on' : '')}
            onClick={() => navigate(`/garage/${c.id}`)}
          >
            {c.nickname}
          </button>
        ))}
        {canManageFleet && (
          <button className="cs-add" onClick={openAddCar} aria-label="Add car">
            <Plus size={15} />
          </button>
        )}
      </div>

      <CarDetail
        car={car}
        garageId={garageId!}
        openAddPart={openAddPart}
        openEditCar={openEditCar}
        openEditPart={openEditPart}
        openAddSafety={openAddSafety}
      />
    </div>
  );
}

function CarDetail({
  car,
  garageId,
  openAddPart,
  openEditCar,
  openEditPart,
  openAddSafety,
}: {
  car: CarDto;
  garageId: string;
  openAddPart: (car: CarDto) => void;
  openEditCar: (car: CarDto) => void;
  openEditPart: (car: CarDto, item: ItemDto) => void;
  openAddSafety: (car: CarDto) => void;
}) {
  const { canManageFleet, canLogMaintenance } = usePermissions();
  const timeline = useTimeline(garageId, car.id);
  const items = useItems(garageId);

  if (timeline.isPending || items.isPending) return <Loading label="Building the forecast…" />;
  if (timeline.isError) return <ErrorState error={timeline.error} onRetry={() => void timeline.refetch()} />;
  if (items.isError) return <ErrorState error={items.error} onRetry={() => void items.refetch()} />;
  if (!timeline.data || !items.data) return <Loading />;

  const carItems = items.data.filter((i) => i.ownerType === 'car' && i.ownerId === car.id);
  // A catalog item has no check conditions and is not safety gear — it is
  // recorded only for its part number and buy link, never tracked. It must stay
  // out of the wear-part groups and get no forecast line, so it is split off here
  // and listed in its own section (the same rule the contract encodes by allowing
  // `triggers: []`).
  const trackedItems = carItems.filter((i) => !isCatalog(i));
  const catalogItems = carItems.filter(isCatalog);
  const groups = groupBySystem(trackedItems);
  const statuses = statusByItem(timeline.data);
  const carEvents = timeline.data.events.filter((e) => e.status === 'planned');

  return (
    <>
      <div className="car-hero">
        <div className="car-hero-id">
          <CarPhoto garageId={garageId} car={car} />
          <div>
            <h1 className="h1">{car.nickname}</h1>
            <div className="car-hero-sub">{describe(car)}</div>
            {/* Owner-only: cars are owner-managed (routes/fleet.ts), so crew never
                see the edit affordance for one. */}
            {canManageFleet && (
              <button className="link car-edit" onClick={() => openEditCar(car)}>
                <Pencil size={12} /> Edit details
              </button>
            )}
          </div>
        </div>
        <div className="car-hero-stats">
          <div>
            <span>{car.useType === 'street' ? 'Odometer' : 'Use'}</span>
            <b>
              {car.useType === 'street' && car.odometer !== null
                ? `${car.odometer.toLocaleString('en-US')} mi`
                : 'Strip only'}
            </b>
          </div>
          <div>
            <span>Planned events</span>
            <b>{carEvents.length}</b>
          </div>
        </div>
      </div>

      {canManageFleet && (
        <div className="add-btns">
          <button className="add-part-btn" onClick={() => openAddPart(car)}>
            <Plus size={15} /> Add wear part to {car.nickname}
          </button>
          {/* The NHRA safety starter: chassis & restraint gear this car needs at
              its ET. A convenience over adding each by hand — never the rulebook. */}
          <button className="add-part-btn add-safety-btn" onClick={() => openAddSafety(car)}>
            <ShieldCheck size={15} /> Add usual safety equipment
          </button>
        </div>
      )}

      <div className="row-h">
        <h2 className="h2">Planned events</h2>
        <span className="row-sub">converts to actual when run</span>
      </div>
      {carEvents.length === 0 ? (
        <Empty
          icon={<CalendarDays size={20} />}
          title="No events planned"
          hint="Without an event, the forecast has nothing to count passes against."
        />
      ) : (
        <div className="ev-strip">
          {carEvents.map((e) => (
            <div className="ev-card" key={e.id}>
              <div className="ev-date">
                <b>{fmtShort(e.date)}</b>
                <span>{daysUntil(e.date)}d</span>
              </div>
              <div className="ev-name">{e.name}</div>
              {e.track && (
                <div className="ev-track">
                  <MapPin size={11} /> {e.track}
                </div>
              )}
              <div className="ev-est">
                <span>est. {e.estPasses} passes</span>
                {e.estMiles > 0 && <span>· {e.estMiles} mi</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {trackedItems.length === 0 ? (
        <Empty
          icon={<Package size={20} />}
          title="No parts tracked on this car"
          hint={
            canManageFleet
              ? 'Add a wear part with a check condition and it will appear in the forecast.'
              : 'The owner has not added any parts to this car yet.'
          }
          action={
            canManageFleet ? (
              <button className="act" onClick={() => openAddPart(car)}>
                <Plus size={12} /> Add part
              </button>
            ) : undefined
          }
        />
      ) : (
        groups.map((group) => (
          <div className="group" key={group.system}>
            <div className="group-h">{group.system}</div>
            <div className="parts">
              {group.items.map((item) => (
                <PartRow
                  key={item.id}
                  item={item}
                  garageId={garageId}
                  timeline={timeline.data}
                  status={statuses.get(item.id) ?? 'good'}
                  onEdit={canManageFleet ? () => openEditPart(car, item) : undefined}
                />
              ))}
            </div>
          </div>
        ))
      )}

      <CatalogSection
        items={catalogItems}
        onEdit={canManageFleet ? (item) => openEditPart(car, item) : undefined}
      />

      <InspectSection garageId={garageId} timeline={timeline.data} canLog={canLogMaintenance} />
    </>
  );
}

function PartRow({
  item,
  garageId,
  timeline,
  status,
  onEdit,
}: {
  item: ItemDto;
  garageId: string;
  timeline: TimelineResponse;
  status: 'good' | 'due' | 'over';
  /** Owner-only: absent for crew, who see maintenance actions but no structural edit. */
  onEdit?: () => void;
}) {
  const { canLogMaintenance } = usePermissions();
  const { planFor, setPlan } = useSchedule();
  const logService = useLogService(garageId);

  const occurrence = firstOccurrence(timeline.occurrences, item.id);
  const cert = timeline.certs.find((c) => c.itemId === item.id);
  const eventLabel = occurrence?.eventId
    ? (timeline.events.find((e) => e.id === occurrence.eventId)?.name ?? 'the event')
    : '';

  return (
    <div className="part">
      <div className="part-main">
        <div className="part-name">{item.name}</div>
        {item.partNumber && <div className="part-pn">{item.partNumber}</div>}
        <div className="trigs">
          {triggerLabels(item).map((label) => (
            <Trigger key={label}>{label}</Trigger>
          ))}
        </div>
        {/* The reorder link the Add Part flow captured (§12 groundwork). */}
        {item.buyUrl && (
          <a className="part-buy" href={item.buyUrl} target="_blank" rel="noreferrer noopener">
            Reorder
          </a>
        )}
      </div>

      <div className="part-meter">
        {cert ? (
          <div className="part-exp">
            <CalendarDays size={12} /> Expires {fmtShort(cert.date)} · {cert.why}
          </div>
        ) : (
          <>
            {/**
             * The mock's "16 / 20 passes" meter is **gone, not ported**, and
             * that is a deliberate refusal rather than an oversight.
             *
             * A progress bar needs usage-since-last-service, and that number is
             * not on the wire: `ItemDto` carries triggers and `lastServicedAt`,
             * while the API keeps its derived usage internal to
             * services/usage.ts. The only ways to draw a bar here would be to
             * re-derive usage in the browser from events and logs — the exact
             * duplication §8 exists to prevent — or to map the status pill onto
             * a plausible-looking percentage, which would be inventing
             * telemetry a racer might actually trust.
             *
             * So the tile says what the server genuinely knows: when it lands.
             * The bar comes back when the contract exposes usage.
             */}
            <div className="part-proj">{projectionNote(occurrence, timeline.today)}</div>
            {occurrence?.choice && (
              <MidEventControl
                occurrence={occurrence}
                eventLabel={eventLabel}
                plan={planFor(item.id)}
                onPick={(plan) => setPlan(item.id, plan)}
              />
            )}
          </>
        )}
      </div>

      <div className="part-right">
        <div className="part-right-top">
          <Pill s={status} />
          {/* Owner-only: editing a part is a structural change (routes/items.ts),
              so crew never see this — same rule as "Add part" and the car edit. */}
          {onEdit && (
            <button className="part-edit" onClick={onEdit} aria-label={`Edit ${item.name}`}>
              <Pencil size={13} />
            </button>
          )}
        </div>
        {/* The crew line: maintenance actions stay, structural controls do not. */}
        {canLogMaintenance && (
          <div className="part-acts">
            <button
              className="act"
              disabled={logService.isPending}
              onClick={() => logService.mutate({ itemId: item.id, body: { servicedAt: today() } })}
            >
              <Check size={12} /> Log service
            </button>
            <UndoButton garageId={garageId} itemId={item.id} />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Undo the most recent service log.
 *
 * Needs the log's id, which means fetching the item's history — so this is
 * lazy: the list is only queried when the button is actually pressed. Fetching
 * every item's history up front to render a button that is rarely clicked would
 * be a request per part on every page load.
 */
function UndoButton({ garageId, itemId }: { garageId: string; itemId: string }) {
  const undo = useUndoLatest(garageId, itemId);

  return (
    <button className="act act-ghost" aria-label="Undo last service" disabled={undo.isPending} onClick={undo.run}>
      <RotateCcw size={12} />
    </button>
  );
}

function useUndoLatest(garageId: string, itemId: string) {
  const undo = useUndoService(garageId);
  const logs = useServiceLogsLazy(garageId, itemId);

  return {
    isPending: undo.isPending || logs.isFetching,
    run: async () => {
      const list = await logs.fetch();
      // Logs come back oldest-first from the repo; the newest is the one "undo"
      // means. Nothing to undo is not an error — just a no-op.
      const latest = list?.at(-1);
      if (latest) undo.mutate({ itemId, logId: latest.id });
    },
  };
}

function useServiceLogsLazy(garageId: string, itemId: string) {
  const queryClient = useQueryClient();
  const [isFetching, setIsFetching] = useState(false);

  return {
    isFetching,
    fetch: async () => {
      setIsFetching(true);
      try {
        // Through the query client, so the result is cached like any other read
        // rather than being a fetch the cache never learns about.
        return await queryClient.fetchQuery({
          queryKey: keys.serviceLogs(garageId, itemId),
          queryFn: () => listServiceLogs(garageId, itemId),
        });
      } finally {
        setIsFetching(false);
      }
    },
  };
}

function InspectSection({
  garageId,
  timeline,
  canLog,
}: {
  garageId: string;
  timeline: TimelineResponse;
  canLog: boolean;
}) {
  const markChecked = useMarkChecked(garageId);
  if (timeline.inspections.length === 0) return null;

  return (
    <div className="group">
      <div className="group-h group-h-muted">
        <CircleDashed size={14} /> Inspect by hand · no metric
      </div>
      <div className="inspect-grid">
        {timeline.inspections.map((inspection) => (
          <InspectCard
            key={inspection.itemId}
            inspection={inspection}
            canLog={canLog}
            isPending={markChecked.isPending}
            onMarkChecked={() => markChecked.mutate({ itemId: inspection.itemId, checkedAt: today() })}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * A catalog item: no check conditions and not safety gear. The engine projects
 * nothing for it (it has no triggers), so it would render as a blank forecast
 * line in a wear-part group — hence its own section instead.
 */
function isCatalog(item: ItemDto): boolean {
  return item.triggers.length === 0 && !item.isSafety;
}

/**
 * The reference-only parts: recorded so a part number and buy link are on hand
 * when something fails, but never tracked and never forecast. Deliberately flat —
 * name, part number, system and the reorder link — with no status pill or meter,
 * because there is genuinely no "due" state to show.
 */
function CatalogSection({ items, onEdit }: { items: ItemDto[]; onEdit?: (item: ItemDto) => void }) {
  if (items.length === 0) return null;

  return (
    <div className="group">
      <div className="group-h group-h-muted">
        <Package size={14} /> Catalog / reference · not tracked
      </div>
      <div className="parts">
        {items.map((item) => (
          <div className="catalog-row" key={item.id}>
            <div className="catalog-row-main">
              <div className="catalog-row-name">{item.name}</div>
              {item.partNumber && <div className="catalog-row-pn">{item.partNumber}</div>}
              {item.buyUrl && (
                <a className="part-buy" href={item.buyUrl} target="_blank" rel="noreferrer noopener">
                  Reorder
                </a>
              )}
            </div>
            <div className="catalog-row-right">
              <span className="catalog-row-sys">{item.system}</span>
              {/* Owner-only, same rule as a wear part's edit — opens AddPart,
                  which reopens in catalog mode for an item with no triggers. */}
              {onEdit && (
                <button className="part-edit" onClick={() => onEdit(item)} aria-label={`Edit ${item.name}`}>
                  <Pencil size={13} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function describe(car: CarDto): string {
  return (
    [car.year, car.make, car.model].filter(Boolean).join(' ') +
      [car.motorCode, car.powerAdder, car.transmission, car.raceClass]
        .filter(Boolean)
        .map((s) => ` · ${s}`)
        .join('') || 'No details yet'
  );
}
